import {
  ChangeDetectorRef,
  DestroyRef,
  effect,
  EnvironmentInjector,
  inject,
  untracked,
  type Signal,
} from '@angular/core';

/**
 * Repaints the calling component when any of `sources` changes, for views
 * under a detached change detector (the schedule dropzones call `cd.detach()`).
 * There, a signal read in the template never reaches a refresh on its own.
 *
 * The effect is created on the `EnvironmentInjector` so that it is a root
 * effect: one created on a node injector is a view effect, which runs only
 * while its view is refreshed — never, for a detached view. It is destroyed
 * with the calling component.
 *
 * The first run only subscribes: it runs after the view's first render, which
 * already showed the current values. A source that changes between that
 * render and the first run, within the same tick, is therefore not repainted
 * until its next change.
 */
export function useRepaintOnChange(sources: Signal<unknown>[]): void {
  const changeDetector = inject(ChangeDetectorRef);
  let isFirstRun = true;

  const repaint = effect(
    () => {
      sources.forEach((source) => source());

      if (isFirstRun) {
        isFirstRun = false;
        return;
      }

      untracked(() => changeDetector.detectChanges());
    },
    { injector: inject(EnvironmentInjector), manualCleanup: true },
  );

  inject(DestroyRef).onDestroy(() => repaint.destroy());
}
